import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import {
  Placeholder,
  //VisitorIdentification,
  LayoutServiceData,
  Field,
} from '@sitecore-jss/sitecore-jss-nextjs';
import TagManager, { TagManagerArgs } from 'react-gtm-module';
import { ThemeName, useTheme } from 'lib/context/ThemeContext';
import MetaData from './helpers/Meta/MetaData';
import { useFavorites } from './lib/utils/useFavorites';
import FixLinks from './helpers/FixLinks/FixLinks';
import HeadScripts from './helpers/HeadScripts/HeadScripts';
import { useRouter } from 'next/router';
import ReactHtmlParser from 'react-html-parser';
import { EwSiteInfo } from 'lib/site/ew-site-info';
import { Feature } from './.generated/Feature.EnterpriseWeb.model';
import { useAffiliate } from 'lib/context/AffiliateContext';
import { CURRENTZIP_SYMBOL, CURRENTSTATE_SYMBOL } from 'lib/affiliate/affiliate-middleware';
import { getCookie } from 'cookies-next';
import { SITE_NAMES } from 'lib/constants/sitenames';
import { SitecoreIds } from 'lib/constants';

// Prefix public assets with a public URL to enable compatibility with Sitecore editors.
// If you're not supporting Sitecore editors, you can remove this.
interface LayoutProps {
  layoutData: LayoutServiceData;
  requestedPath?: string;
  site: EwSiteInfo;
}

const FontLinks: Record<ThemeName, string> = {
  aw: 'https://use.typekit.net/pev3rfw.css',
  rba: 'https://use.typekit.net/shy7gxo.css',
};

const Layout = ({ layoutData, requestedPath, site }: LayoutProps): JSX.Element => {
  const { route } = layoutData.sitecore;

  const { themeName } = useTheme();

  const { favoriteProducts, favoriteProductsCount } = useFavorites();

  const affiliate = useAffiliate();

  const isPageEditing = layoutData.sitecore.context.pageEditing;

  const trustArcCmId = process.env.NEXT_PUBLIC_AW_TRUSTARC_CMID ?? '';

  const tealiumUtagSrc = process.env.NEXT_PUBLIC_TEALIUM_UTAG_SRC ?? '';

  const mainClassPageEditing = isPageEditing ? 'editing-mode' : 'prod-mode';

  const beforeHeadCloseScript = route?.fields?.pageAddScriptsBeforeHeadClose?.value as string;

  const afterBodyOpenScript = route?.fields?.pageAddScriptsAfterBodyOpen?.value as string;

  const beforeBodyCloseScript = route?.fields?.pageAddScriptsBeforeBodyClose?.value as string;

  const isArticlePage =
    layoutData.sitecore?.route?.templateId?.toLowerCase() ===
    SitecoreIds.Project.RenewalByAndersen.Pages.ArticlePage.ItemId.toLowerCase();

  const articleAuthor = layoutData.sitecore?.route?.fields?.articleAuthor?.value as string;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    author: articleAuthor
      ? {
          '@type': 'Person',
          name: articleAuthor,
        }
      : {
          '@type': 'Organization',
          name: 'Renewal By Andersen',
        },
    publisher: {
      '@type': 'Organization',
      name: 'Renewal By Andersen',
    },
    dateModified: layoutData.sitecore?.route?.fields?.lastUpdated?.value,
    datePublished: layoutData.sitecore?.route?.fields?.articleDate?.value,
    headline: layoutData.sitecore?.route?.fields?.articleTitle?.value,
    //@ts-ignore we are using custom fields
    image: [layoutData.sitecore.route?.fields?.primaryImage?.value?.src],
  };

  useEffect(() => {
    const beforeHeadCloseFragment = document
      .createRange()
      .createContextualFragment(beforeHeadCloseScript);
    document.head.append(beforeHeadCloseFragment);

    const afterBodyOpenFragment = document
      .createRange()
      .createContextualFragment(afterBodyOpenScript);
    document.body.prepend(afterBodyOpenFragment);

    const beforeBodyCloseFragment = document
      .createRange()
      .createContextualFragment(beforeBodyCloseScript);
    document.body.append(beforeBodyCloseFragment);

    const consentBannerFragment = document.createElement('div');
    if ((themeName ?? '') == 'aw') {
      consentBannerFragment.id = 'consent-banner';
      document.body.prepend(consentBannerFragment);
    }

    return () => {
      // Handles the case where element is already removed, in that case there would be no parent
      // element and it will do nothing rather than throw an error.
      beforeHeadCloseFragment.parentElement?.removeChild(beforeHeadCloseFragment);
      afterBodyOpenFragment.parentElement?.removeChild(afterBodyOpenFragment);
      beforeBodyCloseFragment.parentElement?.removeChild(beforeBodyCloseFragment);
      if ((themeName ?? '') == 'aw') {
        consentBannerFragment.remove();
      }
    };
  }, [afterBodyOpenScript, beforeBodyCloseScript, beforeHeadCloseScript, themeName]);

  const [fontStyleLoaded, setFontStyleLoaded] = useState(false);
  const router = useRouter();
  const urlPath = router.asPath;

  // On document load, change the font stylesheet back to 'all'.
  // This will make the font load non-blocking to improve load time.
  // Technique from https://www.filamentgroup.com/lab/load-css-simpler/
  // but adopted to work with React because React was re-rendering the link
  // and thus swapping the `media` style each re-rendering causing FOUT.
  useEffect(() => {
    // get link element using querySelecor
    const fontStyleElement = document.querySelector('.fontStyleLink');

    // Update the state once the link is loaded, to update media attribute to 'all'
    if (fontStyleElement) {
      setFontStyleLoaded(true);
    }
  }, []);

  /* Bold Orange datalayer use  for MCP */
  const extractAndCombineTitleValues = (data: Field | undefined): string | undefined => {
    if (!data) {
      return undefined;
    }

    const field = data as unknown as Feature.EnterpriseWeb.Enterprise.Data.Search.FacetTag[];

    if (field?.length > 0) {
      return field.map((item) => item?.fields?.title?.value).join(',');
    }

    return undefined;
  };

  // GTM
  // See https://andersenwindows.atlassian.net/wiki/spaces/EW/pages/3275096077/Local+Google+Tag+Manager+Development for Developer information

  useEffect(() => {
    if (!site.gtmId) {
      return;
    }

    const gtmInitData: TagManagerArgs = {
      gtmId: site.gtmId as string,
      auth: site.gtmAuth as string,
      preview: site.gtmEnvironment as string,
    };

    /* Bold Orange datalayer use  for MCP */
    if (site.name === 'AndersenWindows' && themeName === 'aw') {
      const getCategoryName = () => {
        const fields = layoutData?.sitecore?.route?.fields;
        if (fields && Array.isArray(fields?.windowType) && fields?.windowType?.length > 0) {
          return fields?.windowType;
        } else if (fields && Array.isArray(fields?.doorType)) {
          return fields?.doorType;
        }
        return undefined;
      };
      const buildPageLevels = (pageLevels?: string[]) => {
        // If no page levels or only empty strings, return all levels as 'undefined'
        if (!pageLevels?.length || pageLevels.every((level) => !level)) {
          return {
            page_level_1: 'undefined',
            page_level_2: 'undefined',
            page_level_3: 'undefined',
            page_level_4: 'undefined',
            page_level_5: 'undefined',
          };
        }

        return pageLevels.reduce((levels, level, index) => {
          if (level) {
            levels[`page_level_${index + 1}`] = level.toLowerCase();
          }
          return levels;
        }, {} as Record<string, string>);
      };
      const pageLevels = (layoutData.sitecore.context?.itemPath as string)
        ?.replace(/^\/+|\/+$/g, '')
        ?.split('/')
        ?.filter(Boolean);

      gtmInitData['dataLayer'] = {
        event: 'aw.page_view',
        pageType:
          layoutData.sitecore.route?.fields?.pageType?.value &&
          layoutData.sitecore.route?.fields?.pageType?.value !== ''
            ? layoutData.sitecore.route?.fields?.pageType?.value
            : 'undefined',
        product:
          layoutData.sitecore.route?.fields?.breadcrumbTitle?.value &&
          layoutData.sitecore.route?.fields?.breadcrumbTitle?.value !== ''
            ? layoutData.sitecore.route?.fields?.breadcrumbTitle?.value
            : 'undefined',
        doorType: (() => {
          const val = extractAndCombineTitleValues(layoutData.sitecore.route?.fields?.doorType);
          return val === '' || val === undefined ? 'undefined' : val;
        })(),
        productType: (() => {
          const val = extractAndCombineTitleValues(layoutData.sitecore.route?.fields?.productType);
          return val === '' || val === undefined ? 'undefined' : val;
        })(),
        windowType: (() => {
          const val = extractAndCombineTitleValues(layoutData.sitecore.route?.fields?.windowType);
          return val === '' || val === undefined ? 'undefined' : val;
        })(),
        productSeries: (() => {
          const val = extractAndCombineTitleValues(
            layoutData.sitecore.route?.fields?.productSeries
          );
          return val === '' || val === undefined ? 'undefined' : val;
        })(),
        pageInformation: {
          ...buildPageLevels(pageLevels),
          product_series: (() => {
            const val = extractAndCombineTitleValues(
              layoutData.sitecore.route?.fields?.productSeries
            );
            return val === '' || val === undefined ? 'undefined' : val;
          })(),
          product_type: (() => {
            const val = extractAndCombineTitleValues(
              layoutData.sitecore.route?.fields?.productType
            );
            return val === '' || val === undefined ? 'undefined' : val;
          })(),
          page_type:
            layoutData.sitecore.route?.fields?.pageType?.value &&
            layoutData.sitecore.route?.fields?.pageType?.value !== ''
              ? layoutData.sitecore.route?.fields?.pageType?.value
              : 'undefined',
          category_name: (() => {
            const val = extractAndCombineTitleValues(getCategoryName());
            return val === '' || val === undefined ? 'undefined' : val;
          })(), // Handle multi-select
          product_name:
            layoutData.sitecore.route?.fields?.breadcrumbTitle?.value &&
            layoutData.sitecore.route?.fields?.breadcrumbTitle?.value !== ''
              ? layoutData.sitecore.route?.fields?.breadcrumbTitle?.value
              : 'undefined', // Breadcrumb-based product name
        },
        siteInformation: {
          brand: site.name,
        },
      };
    }

    TagManager.initialize(gtmInitData);
    if (site.hasAffiliatePersonalization) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'marketLoad',
          affiliateInformation: {
            store_number: `${affiliate.userAffiliate.affiliateId}`,
            store_location: `${affiliate.userAffiliate.city}, ${affiliate.userAffiliate.state.abbreviation}`, // City, State
            zip_code: `${getCookie(CURRENTZIP_SYMBOL)}`,
            state: `${getCookie(CURRENTSTATE_SYMBOL)}`, // Two-letter state abbreviation
          },
        },
      });
    }

    const handleDocumentClick = (event: Event) => {
      if (event.target instanceof Element) {
        const target = event.target;

        // Handle click for authored CTAs
        if (target.hasAttribute('data-gtm-click')) {
          const dataLayer: Record<string, string | null> = {};

          for (const name of target.getAttributeNames()) {
            if (name.startsWith('data-gtm-dl-')) {
              const key = name.replace('data-gtm-dl-', '').replaceAll('-', '_');
              dataLayer[key] = target.getAttribute(name);
            }
          }

          TagManager.dataLayer({ dataLayer });
          return;
        }

        // Handle click for RTE CTAs
        // Note: We don't need to handle nav_click gtm event as its not likely to have the RTE in navigation components
        else if (target instanceof HTMLAnchorElement && target.closest('div.body-copy')) {
          TagManager.dataLayer({
            dataLayer: {
              event: target.href.startsWith('tel:') ? 'click_to_call' : 'cta_click',
            },
          });
        }
      }
    };

    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };

    // We can ignore react-hooks/exhaustive-deps warning for this useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.gtmId, router?.asPath]);

  // SEO
  // Included alternate links as per the recommendations from Collective Measures.
  let enHostName = '',
    caHostName = '',
    enUrl = '',
    caUrl = '';

  if (
    site?.name?.toLowerCase() === SITE_NAMES?.RenewalbyAndersen?.toLowerCase() ||
    site?.name?.toLowerCase() === SITE_NAMES?.RenewalbyAndersenCA?.toLowerCase()
  ) {
    const publicUrl = process?.env?.PUBLIC_URL ?? '';
    enHostName = publicUrl;
    caHostName = publicUrl.replace('.com', '.ca');
    enUrl = `${enHostName}${requestedPath ?? ''}`;
    caUrl = `${caHostName}${requestedPath ?? ''}`;
  }

  return (
    <>
      <Head>
        {themeName === 'aw' && (
          <>
            {/* eslint-disable-next-line @next/next/no-sync-scripts */}
            <script
              src={`https://consent.trustarc.com/v2/autoblockasset/core.min.js?cmId=${trustArcCmId}`}
            ></script>
            {/* eslint-disable-next-line @next/next/no-sync-scripts */}
            <script src={`https://consent.trustarc.com/v2/autoblock?cmId=${trustArcCmId}`}></script>
            <script
              type="text/javascript"
              async={true}
              src={`https://consent.trustarc.com/v2/notice/${trustArcCmId}`}
            ></script>
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
            window.addEventListener("message", function(event) {
              var eventDataJson = null;
              try {
                eventDataJson = JSON.parse(event.data);
              } catch (e) {
              }
              // Safeguard to make sure we are only getting events from TrustArc
              if (eventDataJson && eventDataJson.source === "preference_manager") {
                // Means that the user has submited their preferences
                if (eventDataJson.message === "submit_preferences") {
                  setTimeout(function () {
                    window.location.reload();
                  }, 20);
                }
              }
            }, false);
          `,
              }}
            ></script>
          </>
        )}
        {/* https://css-tricks.com/how-to-load-fonts-in-a-way-that-fights-fout-and-makes-lighthouse-happy/ */}
        {/* Preconnect/reload as early as possible */}
        <link rel="preconnect" href="https://use.typekit.net" crossOrigin="" />
        <link rel="preconnect" href="https://p.typekit.net" crossOrigin="" />
        <link rel="preload" href={FontLinks[themeName]} as="style" />
        {themeName === 'aw' && <link rel="icon" href="/awfavicon.svg" />}
        {themeName === 'rba' && <link rel="icon" href="/rbafavicon.png" />}

        {urlPath === '/' &&
          site.name === 'AndersenWindows' &&
          themeName === 'aw' &&
          ReactHtmlParser(
            '<script type="application/ld+json">{"@context":"http://schema.org","@type":"Organization","@id":"https://www.andersenwindows.com/","url":"https://www.andersenwindows.com","email":"Example@example.com","name":"Andersen Windows","telephone":"+1-800-426-4261","logo":"https://techpub1.andersenwindows.com//Publications/Images/AW_Logo.png","sameAs":["https://www.facebook.com/AndersenWindows","https://www.pinterest.com/andersenwindows/","https://www.instagram.com/andersen_windows/","https://www.houzz.com/photos/andersen-windowsphbr0lbl-bl~l_8256","https://www.youtube.com/user/AndersenWindow","https://www.wikidata.org/wiki/Q4753960","https://twitter.com/andersenwindow/123"]}</script>'
          )}
        {/* Load font style as print and swap to all to make it non-blocking */}
        <link
          className="fontStyleLink"
          rel="stylesheet"
          href={FontLinks[themeName]}
          // Delay loading font until document is loaded.  See useEffect above.
          media={fontStyleLoaded ? 'all' : 'print'}
        />
        {requestedPath && isArticlePage && (
          <script
            type="application/ld+json"
            id="article-schema"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
          />
        )}
        <noscript>
          <link rel="stylesheet" href={FontLinks[themeName]} />
        </noscript>
        {(site.name.toLowerCase() === SITE_NAMES?.RenewalbyAndersen.toLowerCase() ||
          site.name.toLowerCase() === SITE_NAMES?.RenewalbyAndersenCA.toLowerCase()) && (
          <>
            <link rel="alternate" hrefLang="x-default" href={enUrl} />
            <link rel="alternate" hrefLang="en" href={enUrl} />
            <link rel="alternate" hrefLang="en-CA" href={caUrl} />
          </>
        )}
      </Head>
      <MetaData requestedPath={requestedPath} hostName={site.canonicalHostName} />
      <HeadScripts />
      {/* <!-- Tealium Universal Tag --> */}
      {tealiumUtagSrc && (
        <>
          {/* eslint-disable-next-line @next/next/no-sync-scripts */}
          <script
            id="tealium-utag"
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html: `(function(a,b,c,d) {
                a='${tealiumUtagSrc}';
                b=document;c='script';d=b.createElement(c);d.src=a;
                d.type='text/javascript';d.async=true;
                a=b.getElementsByTagName(c)[0];a.parentNode.insertBefore(d,a);
              })();`,
            }}
          />
        </>
      )}

      {/*
        VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
        If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
        For XM (CMS-only) apps, this should be removed.

        VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
      */}
      {/*<VisitorIdentification />*/}

      {/* root placeholder for the app, which we add components to using route data */}
      <div className={`${mainClassPageEditing}`}>
        {/* Inline styling is required for any element between the mainClassPageEditing div element and the <Placeholder> so that elements within the header
            can use the main content div for position purposes. */}
        <header className="inline">
          <div id="header" className="inline">
            {route && (
              <Placeholder
                name="headless-header"
                rendering={route}
                favoriteProductsCount={favoriteProductsCount}
              />
            )}
          </div>
        </header>
        <main>
          <div id="hero">
            {route && (
              <Placeholder
                name="headless-hero"
                rendering={route}
                favoriteProducts={favoriteProducts}
              />
            )}
          </div>
          <div id="main">
            {route && (
              <Placeholder
                name="headless-main"
                rendering={route}
                favoriteProducts={favoriteProducts}
                hideStickyForm={route?.fields?.hideStickyForm?.value || false}
              />
            )}
          </div>
          {/* Added per support case CS0429235 until we upgrade to new version of JSS*/}
          <FixLinks></FixLinks>
        </main>
        <div id="pre-footer">
          {route && <Placeholder name="headless-prefooter" rendering={route} />}
        </div>
        <footer>
          <div id="footer">{route && <Placeholder name="headless-footer" rendering={route} />}</div>
        </footer>
      </div>
    </>
  );
};

export default Layout;
